import shouldAddHeaderBetweenAgendaItems from './shouldAddHeaderBetweenAgendaItems';
import { AgendaItem, AgendaItemType } from 'mini-agenda';
import { SUFFIX_AGENDA_SEPARATOR } from './agendaConstants';
import {
    getCalendarEventsCountForADay,
    convertEpochTimeToDisplayDate,
} from 'mini-calendar-events-store';

interface AddedItemsMap {
    [key: string]: AgendaItem;
}

interface PrepareAgendaHeaderItem {
    agendaItemTime: number;
    eventCountForDay: number;
    headerKey: string;
}

/**
 * This function mutates Agenda store to add more items into it
 * @param agendaStore - The current agenda store
 * @param agendaItems - Incoming items that should be added in agenda store
 * @param shouldAddToTop - Flag denotes if items should be added in top of array or at the bottom of array
 */
export default function addAgendaItemsWithMutation(
    agendaStoreItems: AgendaItem[],
    agendaItems: AgendaItem[],
    shouldAddToTop: boolean
) {
    // We will now use a map of id to AgendaItems, since in the case of headers we will need to UPDATE the older object with the new event count
    const addedItems: AddedItemsMap = {};
    for (const item of agendaStoreItems) {
        addedItems[item.id] = item;
    }

    const currentFirstItem = agendaStoreItems.length ? agendaStoreItems[0] : null;
    const currentLastItem = agendaStoreItems.length
        ? agendaStoreItems[agendaStoreItems.length - 1]
        : null;

    let currentIncomingFirstItemIndex = getCurrentIncomingFirstItemIndex(
        agendaItems,
        shouldAddToTop
    );

    currentIncomingFirstItemIndex = skipTerminalDuplicatesInIncomingArray(
        agendaItems,
        currentIncomingFirstItemIndex,
        addedItems,
        shouldAddToTop
    );

    const incomingFirstItem = getIncomingFirstItem(agendaItems, currentIncomingFirstItemIndex);

    if (shouldAddToTop) {
        checkAndAddHeaderItemsForTop(
            incomingFirstItem,
            addedItems,
            currentFirstItem,
            agendaStoreItems
        );
    } else {
        checkAndAddHeaderItemsForBottom(
            incomingFirstItem,
            addedItems,
            currentLastItem,
            agendaStoreItems
        );
    }

    checkAndAddItems(
        agendaItems,
        agendaStoreItems,
        addedItems,
        currentIncomingFirstItemIndex,
        shouldAddToTop
    );
}

/**
 * This function is used to add extra header items to top while adding items in agenda store
 * @param incomingFirstItem - THe first incoming item from to be added in agenda store
 * @param addedItems - The map of already added items
 * @param currentFirstItem - The current first item in agenda store, could also be null
 * @param currentStoreItems - All the items present in agenda store
 */
function checkAndAddHeaderItemsForTop(
    incomingFirstItem: AgendaItem,
    addedItems: AddedItemsMap,
    currentFirstItem: AgendaItem,
    currentStoreItems: AgendaItem[]
) {
    // When we need to add a header at the join boundary when agenda items
    // on both side of boundary are of different days.
    if (shouldAddHeaderBetweenAgendaItems(currentFirstItem, incomingFirstItem)) {
        const { agendaItemTime, eventCountForDay, headerKey } = prepareAgendaHeaderItem(
            currentFirstItem
        );

        checkAndAddDayMonthHeaderItem(
            currentStoreItems,
            addedItems,
            headerKey,
            agendaItemTime,
            eventCountForDay,
            true
        );
    }
}

/**
 * This function is used to add extra header items to bottom while adding items in agenda store
 * @param incomingFirstItem - THe first incoming item from to be added in agenda store
 * @param addedItems - The map of already added items
 * @param currentFirstItem - The current first item in agenda store, could also be null
 * @param currentStoreItems - All the items present in agenda store
 */
function checkAndAddHeaderItemsForBottom(
    incomingFirstItem: AgendaItem,
    addedItems: AddedItemsMap,
    currentLastItem: AgendaItem,
    currentStoreItems: AgendaItem[]
) {
    // Need to add header in the boundary
    if (shouldAddHeaderBetweenAgendaItems(currentLastItem, incomingFirstItem)) {
        const { agendaItemTime, eventCountForDay, headerKey } = prepareAgendaHeaderItem(
            incomingFirstItem
        );

        checkAndAddDayMonthHeaderItem(
            currentStoreItems,
            addedItems,
            headerKey,
            agendaItemTime,
            eventCountForDay,
            false
        );
    }
}

/**
 * This function returns the first item of incomings item to add in Agenda View.
 * @param receivedAgendaItems - All the received agenda items to be added in store
 * @param shouldAddToTop - Flag denoting if incoming items to be added in top or end of Agenda View
 */
function getCurrentIncomingFirstItemIndex(
    receivedAgendaItems: AgendaItem[],
    shouldAddToTop: boolean
): number {
    let currentIncomingFirstItemIndex = -1;
    if (receivedAgendaItems.length) {
        if (shouldAddToTop) {
            // As agenda items will be added on top of current list, we would like to iterate from last to first.
            currentIncomingFirstItemIndex = receivedAgendaItems.length - 1;
        } else {
            currentIncomingFirstItemIndex = 0;
        }
    }
    return currentIncomingFirstItemIndex;
}

/**
 * This function is needed to remove any duplicate items that exists in incoming agenda items to be added
 * @param receivedAgendaItems - The incoming agenda items
 * @param currentIncomingFirstItemIndex - Already computed first item to be added(which could be duplicate)
 * @param addedItems - The map of already added items
 * @param shouldAddToTop - Flag denoting if incoming items to be added in top or end of Agenda View
 */
function skipTerminalDuplicatesInIncomingArray(
    receivedAgendaItems: AgendaItem[],
    currentIncomingFirstItemIndex: number,
    addedItems: AddedItemsMap,
    shouldAddToTop: boolean
): number {
    let currIndex = currentIncomingFirstItemIndex;
    while (
        currIndex > -1 &&
        currIndex < receivedAgendaItems.length &&
        addedItems[receivedAgendaItems[currIndex].id]
    ) {
        // Sometimes we can have duplicates where the incoming id is already present due to multiple reasons, in that case simply skip them in receivedAgendaItems
        if (shouldAddToTop) {
            currIndex--;
        } else {
            currIndex++;
        }
    }
    return currIndex;
}

/**
 * Function to get first item to be added in agenda view store.
 * @param receivedAgendaItems - The incoming agenda items
 * @param currentIncomingFirstItemIndex - Already computed first item to be added(which could be duplicate)
 */
function getIncomingFirstItem(
    receivedAgendaItems: AgendaItem[],
    currentIncomingFirstItemIndex: number
): AgendaItem {
    return currentIncomingFirstItemIndex > -1 &&
        currentIncomingFirstItemIndex < receivedAgendaItems.length
        ? receivedAgendaItems[currentIncomingFirstItemIndex]
        : null;
}

/**
 * Function to prepare header type agenda item
 * @param targetItem - The targeted item that should be converted into PrepareAgendaHeaderItem
 */
function prepareAgendaHeaderItem(targetItem: AgendaItem): PrepareAgendaHeaderItem {
    const agendaItemTime = targetItem.timestamp;
    const eventCountForDay = getCalendarEventsCountForADay(
        convertEpochTimeToDisplayDate(agendaItemTime)
    );
    const headerKey: string = agendaItemTime + SUFFIX_AGENDA_SEPARATOR;
    return {
        agendaItemTime,
        eventCountForDay,
        headerKey,
    };
}

/**
 * This function tests if the item passed into it should be added as a day header item in AgendaView
 * @param storeItems - The current agenda store items
 * @param addedItems - The map of already added items
 * @param headerKey - The key of header type agenda item
 * @param agendaItemTime - The timestamp of agenda item to be added as header
 * @param eventCountForDay - No. of events in that day
 * @param shouldAddToTop - Flag denoting if incoming items to be added in top or end of Agenda View
 */
function checkAndAddDayMonthHeaderItem(
    storeItems: AgendaItem[],
    addedItems: AddedItemsMap,
    headerKey: string,
    agendaItemTime: number,
    eventCountForDay: number,
    shouldAddToTop: boolean
) {
    if (!addedItems[headerKey]) {
        const headerItem = addDayMonthHeaderItem(headerKey, agendaItemTime, eventCountForDay);
        if (shouldAddToTop) {
            storeItems.unshift(headerItem);
        } else {
            storeItems.push(headerItem);
        }
        addedItems[headerKey] = headerItem;
    } else {
        // update the event count for the existing header
        addedItems[headerKey].eventCount = eventCountForDay;
    }
}

/**
 * This function forms and returns a day header item
 * @param headerKey - The key of header type agenda item
 * @param agendaItemTime - The timestamp of agenda item to be added as header
 * @param eventCountForDay - No. of events in that day
 */
function addDayMonthHeaderItem(
    headerKey: string,
    agendaItemTime: number,
    eventCountForDay: number
): AgendaItem {
    const headerItem: AgendaItem = {
        type: AgendaItemType.DayMonthSeparator,
        id: headerKey,
        timestamp: agendaItemTime,
        eventCount: eventCountForDay,
    };
    return headerItem;
}

/**
 * This function checks if incoming agenda items should be added in store
 * @param receivedAgendaItems - The incoming agenda items to be added
 * @param storeItems - The current agenda store items
 * @param addedItems - The map of already added items
 * @param currentIncomingFirstItemIndex - Already computed first item to be added(which could be duplicate)
 * @param shouldAddToTop - Flag denoting if incoming items to be added in top or end of Agenda View
 */
function checkAndAddItems(
    receivedAgendaItems: AgendaItem[],
    storeItems: AgendaItem[],
    addedItems: AddedItemsMap,
    currentIncomingFirstItemIndex: number,
    shouldAddToTop: boolean
) {
    if (receivedAgendaItems.length) {
        // add the elements in the reversed order
        addItems(
            receivedAgendaItems,
            storeItems,
            addedItems,
            currentIncomingFirstItemIndex,
            shouldAddToTop
        );
    }
}

/**
 * This function adds all the incoming agenda items in store
 * @param receivedAgendaItems - The incoming agenda items to be added
 * @param storeItems - The current agenda store items
 * @param addedItems - The map of already added items
 * @param currentIncomingFirstItemIndex - Already computed first item to be added(which could be duplicate)
 * @param shouldAddToTop - Flag denoting if incoming items to be added in top or end of Agenda View
 */
function addItems(
    receivedAgendaItems: AgendaItem[],
    storeItems: AgendaItem[],
    addedItems: AddedItemsMap,
    currentIncomingFirstItemIndex: number,
    shouldAddToTop: boolean
) {
    let currentItemToAddIndex: number = currentIncomingFirstItemIndex;
    while (
        shouldAddToTop
            ? currentItemToAddIndex >= 0
            : currentItemToAddIndex < receivedAgendaItems.length
    ) {
        const itemToAdd = receivedAgendaItems[currentItemToAddIndex];

        // Increment the index
        if (shouldAddToTop) {
            currentItemToAddIndex--;
        } else {
            currentItemToAddIndex++;
        }

        // If the item is already in map then continue
        if (addedItems[itemToAdd.id]) {
            continue;
        }

        // Do the mutation
        if (shouldAddToTop) {
            storeItems.unshift(itemToAdd);
        } else {
            storeItems.push(itemToAdd);
        }
        addedItems[itemToAdd.id] = itemToAdd;
    }
}
